import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, List, ListItem, ListItemText, Typography } from '@mui/material';
import React from 'react';

import { useAdGroupsByIds } from '../../hooks/useAdGroupsByIds';

interface DeleteAppIdentifierConfirmationProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  appIdentifier: string;
  targetingProfiles?: number[];
  isLoading: boolean;
}

function DeleteAppIdentifierConfirmation({
  open,
  onClose,
  onConfirm,
  appIdentifier,
  targetingProfiles,
  isLoading
}: DeleteAppIdentifierConfirmationProps) {
  // Convert the targeting profiles array to strings for the hook
  const targetingProfileIds = targetingProfiles?.map(id => id.toString()) || [];
  const { data, isLoading: loadingAdGroups } = useAdGroupsByIds(targetingProfileIds, open);

  const adGroups = data?.adGroups || [];
  const adGroupCount = targetingProfiles?.length || 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Delete App Identifier
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" gutterBottom>
          Are you sure you want to delete the app identifier <strong>{appIdentifier}</strong>?
        </Typography>
        
        {adGroupCount > 0 && (
          <>
            <Typography variant="body1" color="error" sx={{ mt: 2, mb: 1 }}>
              Warning: This app identifier is used by {adGroupCount} AdGroup{adGroupCount !== 1 ? 's' : ''}. 
              Deleting it will affect all of them.
            </Typography>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Affected AdGroups:
            </Typography>
            
            {loadingAdGroups ? (
              <CircularProgress size={24} sx={{ display: 'block', mx: 'auto', my: 2 }} />
            ) : (
              <List dense sx={{ maxHeight: '200px', overflow: 'auto', bgcolor: 'background.paper' }}>
                {adGroups.map((adGroup) => (
                  <ListItem key={adGroup.id}>
                    <ListItemText primary={adGroup.name} />
                  </ListItem>
                ))}
              </List>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button 
          onClick={onConfirm} 
          color="error" 
          variant="contained" 
          disabled={isLoading}
        >
          {isLoading ? <CircularProgress size={24} /> : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DeleteAppIdentifierConfirmation;